/**
 * API client.
 *
 * Same-origin, so the session cookie rides along automatically — there is no
 * token to store and nothing to attach by hand. `credentials: 'same-origin'`
 * is explicit rather than relied upon, because a future change to a different
 * origin should break loudly here rather than silently stop sending the cookie.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Nest returns an array for a validation failure. Kept separate from
     * `message` so a form can list every problem instead of showing the first
     * and making the user resubmit to discover the second.
     */
    readonly problems: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    let message = response.statusText;
    let problems: string[] = [];
    try {
      const parsed = (await response.json()) as { message?: string | string[] };
      if (Array.isArray(parsed.message)) {
        problems = parsed.message;
        message = parsed.message.join('; ');
      } else if (parsed.message) {
        message = parsed.message;
      }
    } catch {
      // Non-JSON error body; the status text will do.
    }
    throw new ApiError(response.status, message, problems);
  }

  // 204, or any empty body.
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const apiGet = <T>(path: string): Promise<T> => request<T>('GET', path);
export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>('POST', path, body);
export const apiPatch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>('PATCH', path, body);
export const apiPut = <T>(path: string, body: unknown): Promise<T> => request<T>('PUT', path, body);
export const apiDelete = <T>(path: string): Promise<T> => request<T>('DELETE', path);
